import { isValidElement } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import isString from "lodash-es/isString"
import { useBoolean } from "react-use"

import { Button, ETypographyColor, Typography } from "@/3514/components"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import { withPressHandler } from "@/3514/utils"
import Avatar from "@/shared/avatar/Avatar"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import { CoachingChatFeedbackIcon as FeedbackIcon } from "../assets"
import { useCoachingChatContext as useChatContext } from "../context"
import { useCoachingChatConversationMessageByIdSelector as useMessageSelector } from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatMessageAuthor as EMessageAuthor,
    ECoachingChatConversationMessagePhase as EMessagePhase,
    type TCoachingChatConversationMessage as TMessage
} from "../types"

import { CoachingChatMessageLight as LightMessage } from "./coaching-chat.message-light.component"
import { coachingChatOwlComponents as owls } from "./coaching-chat.owls-components"
import { CoachingChatTypingIndicator as TypingIndicator } from "./coaching-chat.typing-indicator.component"

type TProps = {
    clientMessageId: string
}

const { isStringPlainText } = richTextRendererUtils
const {
    conversation: { message: classes }
} = chatStyles

const avatarDimension: number = 30

const MessageFull: FC<TProps> = ({ clientMessageId }: TProps): ReactElement => {
    const {
        entityId,
        feedback: { handleModalOpen },
        conversation: { renderMessage: messageRenderer, user }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const {
        content: messageText,
        author,
        id: messageId,
        status
    }: TMessage = useMessageSelector(entityId, clientMessageId)

    const isUserMessage: boolean = eq(author, EMessageAuthor.User)

    const [isIconVisible, setIsIconVisible]: ReturnType<typeof useBoolean> = useBoolean(false)

    const handleFeedbackAction: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        handleModalOpen({
            variant: EFeedbackModalVariant.ChatMessage,
            type: EFeedbackModalType.GeneralExtended,
            payload: {
                messageId,
                renderDetail: (): ReactElement => (
                    <LightMessage clientMessageId={clientMessageId} iconFillColor="blue-500" />
                )
            },
            modalTitle: "entities.coachingChat.feedbackModal.message.title"
        })
    )

    function renderMessage(): ReactElement {
        return isString(messageText) ? (
            isStringPlainText(messageText as string) || isUserMessage ? (
                <Typography text={messageText} color={ETypographyColor.White} isPretty withHyphens />
            ) : (
                messageRenderer(messageText as string)
            )
        ) : (
            isValidElement(messageText) && messageText
        )
    }

    return eq(status, EMessagePhase.Waiting) ? (
        <TypingIndicator />
    ) : eq(status, EMessagePhase.Done) ? (
        <div className={classes.container({ author, isNotInteractive: isUserMessage, isLight: false })}>
            <div className={classes.messageRow({ author })}>
                <div className={classes.messageWrapper()}>
                    <div className={classes.message({ author })}>{renderMessage()}</div>

                    {!isUserMessage && (
                        <Button
                            isContainer
                            className={classes.messageFeedbackButton()}
                            onClick={handleFeedbackAction}
                            onKeyDown={handleFeedbackAction}
                            onMouseEnter={(): void => setIsIconVisible(true)}
                            onMouseLeave={(): void => setIsIconVisible(false)}
                        >
                            <FeedbackIcon isActive={isIconVisible} />
                        </Button>
                    )}
                </div>

                {isUserMessage ? (
                    <Avatar
                        width={avatarDimension}
                        height={avatarDimension}
                        url={user?.photo}
                        alt={`${user?.firstName} avatar`}
                        imgClassName={`min-w-[${avatarDimension}px]`}
                    />
                ) : (
                    <owls.DefaultLogo fill="blue-600" />
                )}
            </div>
        </div>
    ) : null
}

MessageFull.displayName = "CoachingChatMessageFull"

export { MessageFull as CoachingChatMessageFull }
